/**
 * LEMO - Galerie Photos Restaurant
 * Permet aux restaurants de gérer leur galerie (max 6 photos)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  Upload, 
  X, 
  Loader2, 
  GripVertical,
  Plus,
  Trash2,
  Eye,
  Star,
  Crown,
  RotateCw,
  RotateCcw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../../utils/images';

const MAX_PHOTOS = 6;

export default function RestaurantGallery() {
  useAuth();
  const [gallery, setGallery] = useState([]);
  const [featuredImageUrl, setFeaturedImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [settingFeatured, setSettingFeatured] = useState(false);
  const [rotatingIndex, setRotatingIndex] = useState(null);
  const fileInputRef = useRef(null);

  // Charger la galerie
  const loadGallery = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/gallery`);
      setGallery(response.data.gallery || []);
      setFeaturedImageUrl(response.data.featured_image_url || null);
    } catch (error) {
      toast.error('Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  // Upload de photo
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Vérifier le nombre total de photos
    const remainingSlots = MAX_PHOTOS - gallery.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos autorisées`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    
    setUploading(true);
    
    try {
      for (const file of filesToUpload) {
        // Vérifier la taille (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 5 Mo)`);
          continue;
        }

        // Vérifier le type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} n'est pas une image`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Ajouter à la galerie
        await axios.post(`${API}/restaurant-dashboard/gallery/add`, 
          { url: uploadResponse.data.url }
        );
      }
      
      toast.success('Photo(s) ajoutée(s)');
      loadGallery();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer une photo
  const handleRemovePhoto = async (index) => {
    try {
      await axios.delete(`${API}/restaurant-dashboard/gallery/${index}`);
      toast.success('Photo supprimée');
      loadGallery();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Définir comme photo mise en avant
  const handleSetFeatured = async (photoUrl) => {
    setSettingFeatured(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/gallery/featured`, 
        { url: photoUrl }
      );
      setFeaturedImageUrl(photoUrl);
      toast.success('Photo mise en avant définie !');
    } catch (error) {
      toast.error('Erreur lors de la définition');
    } finally {
      setSettingFeatured(false);
    }
  };

  // Retirer la photo mise en avant
  const handleRemoveFeatured = async () => {
    setSettingFeatured(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/gallery/featured`, 
        { url: null }
      );
      setFeaturedImageUrl(null);
      toast.success('Photo mise en avant retirée');
    } catch (error) {
      toast.error('Erreur lors du retrait');
    } finally {
      setSettingFeatured(false);
    }
  };

  // Réorganiser les photos (drag & drop simplifié)
  const movePhoto = async (fromIndex, toIndex) => {
    const newGallery = [...gallery];
    const [movedItem] = newGallery.splice(fromIndex, 1);
    newGallery.splice(toIndex, 0, movedItem);
    
    try {
      await axios.put(`${API}/restaurant-dashboard/gallery`, 
        { gallery: newGallery }
      );
      setGallery(newGallery);
    } catch (error) {
      toast.error('Erreur lors de la réorganisation');
    }
  };

  // Rotation d'image (90° horaire ou antihoraire)
  const handleRotatePhoto = async (index, direction) => {
    setRotatingIndex(index);
    try {
      const photo = gallery[index];
      const photoUrl = getFullImageUrl(photo);
      
      // Charger l'image dans un canvas et pivoter
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = photoUrl;
      });
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const angle = direction === 'cw' ? 90 : -90;
      
      canvas.width = img.height;
      canvas.height = img.width;
      
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.9));
      
      const formData = new FormData();
      formData.append('file', blob, 'rotated.webp');
      
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newUrl = uploadResponse.data.url;
      const newGallery = [...gallery];
      newGallery[index] = newUrl;
      
      await axios.put(`${API}/restaurant-dashboard/gallery`,
        { gallery: newGallery }
      );
      
      if (isFeaturedPhoto(photo)) {
        await axios.put(`${API}/restaurant-dashboard/gallery/featured`,
          { url: newUrl }
        );
        setFeaturedImageUrl(newUrl);
      }
      
      setGallery(newGallery);
      toast.success('Image pivotée');
    } catch (error) {
      console.error('Rotation error:', error);
      toast.error('Erreur lors de la rotation');
    } finally {
      setRotatingIndex(null);
    }
  };

  // Vérifier si une photo est la mise en avant
  const isFeaturedPhoto = (photo) => {
    const fullUrl = getFullImageUrl(photo);
    const featuredFull = getFullImageUrl(featuredImageUrl);
    return photo === featuredImageUrl || fullUrl === featuredFull || photo === featuredImageUrl?.replace(BACKEND_URL, '');
  };

  if (loading) {
    return (
      <DashboardLayout title="Galerie Photos">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Galerie Photos">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-6 h-6 text-emerald-500" />
                Galerie Photos
              </h2>
              <p className="text-gray-500 mt-1">
                Ajoutez jusqu'à {MAX_PHOTOS} photos pour présenter votre établissement aux clients
              </p>
            </div>
            <div className="text-right">
              <span className={`text-2xl font-bold ${gallery.length >= MAX_PHOTOS ? 'text-red-500' : 'text-emerald-500'}`}>
                {gallery.length}
              </span>
              <span className="text-gray-400">/{MAX_PHOTOS}</span>
            </div>
          </div>
        </div>

        {/* Photo mise en avant */}
        {featuredImageUrl && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-amber-300 shadow-md shrink-0">
                <img
                  src={getFullImageUrl(featuredImageUrl)}
                  alt="Photo mise en avant"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Photo mise en avant
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  Cette photo sera affichée en priorité sur la carte de votre commerce dans les listings.
                </p>
                <button
                  onClick={handleRemoveFeatured}
                  disabled={settingFeatured}
                  className="mt-3 text-sm text-amber-600 hover:text-amber-800 underline"
                >
                  Retirer la mise en avant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Zone d'upload */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*"
            multiple
            data-testid="gallery-file-input"
          />
          
          {gallery.length < MAX_PHOTOS && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-center"
              data-testid="gallery-upload-button"
            >
              {uploading ? (
                <Loader2 className="w-10 h-10 mx-auto mb-3 text-emerald-500 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              )}
              <p className="font-medium text-gray-700">
                {uploading ? 'Upload en cours...' : 'Cliquez pour ajouter des photos'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                JPG, PNG, WebP • Max 5 Mo par photo
              </p>
            </button>
          )}

          {gallery.length >= MAX_PHOTOS && (
            <div className="text-center py-4 bg-amber-50 rounded-xl">
              <p className="text-amber-700 font-medium">
                Vous avez atteint le maximum de {MAX_PHOTOS} photos
              </p>
              <p className="text-sm text-amber-600 mt-1">
                Supprimez une photo pour en ajouter une nouvelle
              </p>
            </div>
          )}
        </div>

        {/* Galerie */}
        {gallery.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Vos photos</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((photo, index) => {
                const isFeatured = isFeaturedPhoto(photo);
                const photoFullUrl = getFullImageUrl(photo);
                
                return (
                  <div
                    key={index}
                    className={`relative group aspect-square rounded-xl overflow-hidden border-2 ${
                      isFeatured ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200'
                    }`}
                    data-testid={`gallery-photo-${index}`}
                  >
                    <img
                      src={photoFullUrl}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Badge mise en avant */}
                    {isFeatured && (
                      <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg">
                        <Crown className="w-3 h-3" />
                        Principale
                      </div>
                    )}
                    
                    {/* Overlay avec actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      {/* Bouton définir comme principale */}
                      {!isFeatured && (
                        <button
                          onClick={() => handleSetFeatured(photo)}
                          disabled={settingFeatured}
                          className="w-full px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors flex items-center justify-center gap-1"
                          data-testid={`set-featured-${index}`}
                        >
                          <Crown className="w-4 h-4" />
                          Définir comme principale
                        </button>
                      )}
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRotatePhoto(index, 'ccw')}
                          disabled={rotatingIndex === index}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Pivoter à gauche"
                          data-testid={`rotate-ccw-${index}`}
                        >
                          {rotatingIndex === index ? <Loader2 className="w-5 h-5 text-gray-700 animate-spin" /> : <RotateCcw className="w-5 h-5 text-gray-700" />}
                        </button>
                        <button
                          onClick={() => handleRotatePhoto(index, 'cw')}
                          disabled={rotatingIndex === index}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Pivoter à droite"
                          data-testid={`rotate-cw-${index}`}
                        >
                          {rotatingIndex === index ? <Loader2 className="w-5 h-5 text-gray-700 animate-spin" /> : <RotateCw className="w-5 h-5 text-gray-700" />}
                        </button>
                        <button
                          onClick={() => setPreviewImage(photoFullUrl)}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Voir"
                        >
                          <Eye className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => handleRemovePhoto(index)}
                          className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                          title="Supprimer"
                          data-testid={`delete-photo-${index}`}
                        >
                          <Trash2 className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>

                    {/* Numéro de la photo */}
                    {!isFeatured && (
                      <div className="absolute top-2 left-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">{index + 1}</span>
                      </div>
                    )}

                    {/* Boutons de réorganisation */}
                    {index > 0 && (
                      <button
                        onClick={() => movePhoto(index, index - 1)}
                        className="absolute top-2 right-10 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Déplacer vers la gauche"
                      >
                        <span className="text-xs">←</span>
                      </button>
                    )}
                    {index < gallery.length - 1 && (
                      <button
                        onClick={() => movePhoto(index, index + 1)}
                        className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Déplacer vers la droite"
                      >
                        <span className="text-xs">→</span>
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Cases vides pour montrer les emplacements restants */}
              {Array.from({ length: MAX_PHOTOS - gallery.length }).map((_, index) => (
                <button
                  key={`empty-${index}`}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
                >
                  <Plus className="w-8 h-8 text-gray-300" />
                  <span className="text-xs text-gray-400 mt-1">Ajouter</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Conseils */}
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6">
          <h3 className="font-semibold text-emerald-800 mb-3">Conseils pour de belles photos</h3>
          <ul className="space-y-2 text-sm text-emerald-700">
            <li>• Utilisez une bonne luminosité naturelle</li>
            <li>• Montrez votre devanture, votre intérieur et vos plats</li>
            <li>• Évitez les photos floues ou mal cadrées</li>
            <li className="font-medium">• Définissez une photo "principale" pour qu'elle apparaisse sur votre carte dans les listings !</li>
          </ul>
        </div>
      </div>

      {/* Modal de prévisualisation */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-full max-h-[90vh] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </DashboardLayout>
  );
}
